import { setCatalogPromotion } from '@/lib/api/catalog/catalog-api'
import { mask, unMask } from '@/lib/form-state/util'
import { useState } from 'react'
import { MdLocalOffer } from 'react-icons/md'
import { toast } from 'react-toastify'

const currencyFormat = (value: string | number | null | undefined): string => {
    const digits = `${value ?? ''}`.replace(/[^0-9]+/g, '')
    if (!digits || /^0+$/.test(digits)) return ''
    return mask(digits, '', 'currency', {
        prefix: 'R$ ',
        decimalSeparator: ',',
        groupSeparator: '.',
        precision: 2,
    })
}

const toCents = (value: string): number => parseInt(unMask(value, 'currency') || '0', 10) || 0

/** `datetime-local` pede o horário LOCAL, sem fuso: `toISOString` daria o de Greenwich. */
const toLocalInput = (iso?: string | null): string => {
    if (!iso) return ''
    const date = new Date(iso)
    const local = new Date(date.getTime() - date.getTimezoneOffset() * 60000)
    return local.toISOString().slice(0, 16)
}

const formatDateTime = (iso: string) =>
    new Date(iso).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' })

export interface PromotionValues {
    promotionalPrice?: number | null
    promotionalStart?: string | null
    promotionalEnd?: string | null
}

/**
 * Preço promocional do produto NESTA filial, com início e fim.
 *
 * Grava à parte do "Salvar" do cadastro: a promoção tem regra própria (menor que o preço
 * de venda, período válido) e o operador costuma mexer nela sem tocar no resto do item.
 * A vitrine, o carrinho e o checkout aplicam o promocional só dentro do período.
 */
export default function CatalogPromotionSection({
    catalogId,
    companyId,
    sellingPrice,
    initial,
}: {
    catalogId: number
    companyId: number
    /** Preço de venda gravado na filial, em centavos — o teto da promoção. */
    sellingPrice: number
    initial: PromotionValues
}) {
    const [saved, setSaved] = useState<PromotionValues>(initial)
    const [price, setPrice] = useState(currencyFormat(initial.promotionalPrice))
    const [start, setStart] = useState(toLocalInput(initial.promotionalStart))
    const [end, setEnd] = useState(toLocalInput(initial.promotionalEnd))
    const [saving, setSaving] = useState(false)

    const now = new Date()
    const hasPromotion = !!saved.promotionalPrice
    const status = !hasPromotion
        ? null
        : saved.promotionalEnd && new Date(saved.promotionalEnd) < now
          ? { label: 'Encerrada', className: 'badge-ghost' }
          : saved.promotionalStart && new Date(saved.promotionalStart) > now
            ? { label: 'Agendada', className: 'badge-info' }
            : { label: 'No ar', className: 'badge-success' }

    const cents = toCents(price)
    const discountPercent =
        cents > 0 && sellingPrice > 0 && cents < sellingPrice
            ? Math.round((1 - cents / sellingPrice) * 100)
            : null

    const save = async (clear: boolean) => {
        if (!clear) {
            if (!(cents > 0)) {
                toast.error('Informe o preço promocional')
                return
            }
            if (!start || !end) {
                toast.error('Informe o início e o fim da promoção')
                return
            }
        }
        setSaving(true)
        try {
            const response = await setCatalogPromotion(catalogId, {
                companyId,
                promotionalPrice: clear ? null : cents,
                ...(clear
                    ? {}
                    : {
                          promotionalStart: new Date(start).toISOString(),
                          promotionalEnd: new Date(end).toISOString(),
                      }),
            })
            if (response.success && response.result) {
                setSaved(response.result)
                if (clear) {
                    setPrice('')
                    setStart('')
                    setEnd('')
                }
                toast.success(clear ? 'Promoção encerrada' : 'Promoção salva')
            } else {
                toast.error((response as any).message || 'Não foi possível salvar a promoção')
            }
        } finally {
            setSaving(false)
        }
    }

    return (
        <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-6">
            <div className="flex items-center justify-between gap-3 mb-1">
                <div className="flex items-center gap-2">
                    <MdLocalOffer className="text-xl text-secondary" />
                    <h2 className="text-lg font-semibold text-base-content">
                        Preço promocional
                    </h2>
                    {status && (
                        <span className={`badge badge-sm ${status.className}`}>
                            {status.label}
                        </span>
                    )}
                </div>
            </div>
            <p className="text-sm text-base-content/60 mb-5">
                Vale nesta filial, na loja online, durante o período. Fora dele, volta o
                preço de venda.
                {hasPromotion && saved.promotionalStart && saved.promotionalEnd && (
                    <>
                        {' '}
                        Atual: {currencyFormat(saved.promotionalPrice)} de{' '}
                        {formatDateTime(saved.promotionalStart)} a{' '}
                        {formatDateTime(saved.promotionalEnd)}.
                    </>
                )}
            </p>

            {sellingPrice > 0 ? (
                <>
                    <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
                        <div className="form-control">
                            <label className="label">
                                <span className="label-text font-medium">Preço promocional</span>
                            </label>
                            <input
                                type="text"
                                inputMode="numeric"
                                className="input input-bordered w-full"
                                placeholder="R$ 0,00"
                                value={price}
                                onChange={e => setPrice(currencyFormat(e.target.value))}
                            />
                            <label className="label">
                                <span className="label-text-alt text-base-content/50">
                                    De {currencyFormat(sellingPrice)}
                                    {discountPercent != null && ` (${discountPercent}% de desconto)`}
                                </span>
                            </label>
                        </div>
                        <div className="form-control">
                            <label className="label">
                                <span className="label-text font-medium">Início</span>
                            </label>
                            <input
                                type="datetime-local"
                                className="input input-bordered w-full"
                                value={start}
                                onChange={e => setStart(e.target.value)}
                            />
                        </div>
                        <div className="form-control">
                            <label className="label">
                                <span className="label-text font-medium">Fim</span>
                            </label>
                            <input
                                type="datetime-local"
                                className="input input-bordered w-full"
                                value={end}
                                onChange={e => setEnd(e.target.value)}
                            />
                        </div>
                    </div>
                    <div className="mt-4 flex justify-end gap-2">
                        {hasPromotion && (
                            <button
                                type="button"
                                className="btn btn-ghost btn-sm text-error"
                                disabled={saving}
                                onClick={() => save(true)}
                            >
                                Encerrar promoção
                            </button>
                        )}
                        <button
                            type="button"
                            className="btn btn-secondary btn-sm"
                            disabled={saving}
                            onClick={() => save(false)}
                        >
                            {saving && <span className="loading loading-spinner loading-xs" />}
                            Salvar promoção
                        </button>
                    </div>
                </>
            ) : (
                <p className="text-sm text-base-content/50">
                    Salve o preço de venda nesta filial para criar uma promoção.
                </p>
            )}
        </div>
    )
}
